// src/components/settings/Settings.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../Details.css";
import "./Settings.css";
import { Link } from "react-router-dom";
import Navbar from "../navbar";
import { useAuth } from "../../auth/AuthContext";
import AiSettingsCard from "./AiSettingsCard";

const LICENSE_API_URL = "https://app.tablemate.net/licensor/fetchLicenseInfo.php";
const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";

// PHP timezone endpoint
const TIMEZONE_API_URL = "tablemate/endpoints/utility/phpTimezone.php";

// Payment methods config (restaurant local server)
const PAYMENT_METHODS_API_URL = "tablemate/endpoints/bill/paymentMethods.php";

// Restaurant feature tools (Split Bill, etc.)
const RESTAURANT_TOOLS_API_URL = "tablemate/endpoints/utility/restaurantTools.php";

// Backup status & manual run endpoints
const BACKUP_STATUS_API_URL = "tablemate/endpoints/utility/backupStatus.php";
const RUN_BACKUP_API_URL = "tablemate/endpoints/utility/runBackup.php";

// Optional: endpoint to tail log (recommended to create)
// const BACKUP_LOG_API_URL = "tablemate/endpoints/utility/getBackupLog.php";

function Settings() {
  const pageTitle = "Settings";

  const { user, tenantServerUrl, accessToken, selectedRestaurantId } = useAuth();

  /* -----------------------------
      License State
  ------------------------------*/
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseError, setLicenseError] = useState(null);
  const [showLicenseKey, setShowLicenseKey] = useState(false);

  /* -----------------------------
      Timezone State
  ------------------------------*/
  const [timezone, setTimezone] = useState("");
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneError, setTimezoneError] = useState(null);
  const [timezoneSuccess, setTimezoneSuccess] = useState(null);

  /* -----------------------------
      Payment Methods State
  ------------------------------*/
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [paymentGatewaySamples, setPaymentGatewaySamples] = useState([]);
  const [paymentPlaceholders, setPaymentPlaceholders] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsSaving, setPaymentMethodsSaving] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState(null);
  const [paymentMethodsSuccess, setPaymentMethodsSuccess] = useState(null);

  /* -----------------------------
      Restaurant Tools State
  ------------------------------*/
  const [restaurantTools, setRestaurantTools] = useState([]);
  const [restaurantToolsLoading, setRestaurantToolsLoading] = useState(false);
  const [restaurantToolsSaving, setRestaurantToolsSaving] = useState(false);
  const [restaurantToolsError, setRestaurantToolsError] = useState(null);
  const [restaurantToolsSuccess, setRestaurantToolsSuccess] = useState(null);

  const commonTimezones = [
    "Africa/Gaborone",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Berlin",
    "Asia/Dubai",
    "Asia/Hong_Kong",
    "Asia/Tokyo",
    "UTC",
  ];

  /* -----------------------------
      Backup Status + Progress State
  ------------------------------*/
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupStatusLoading, setBackupStatusLoading] = useState(false);
  const [backupStatusError, setBackupStatusError] = useState(null);

  const [backupTriggerLoading, setBackupTriggerLoading] = useState(false);
  const [backupTriggerError, setBackupTriggerError] = useState(null);
  const [backupTriggerSuccess, setBackupTriggerSuccess] = useState(null);

  // Progress UI helpers
  const [isBackupRunning, setIsBackupRunning] = useState(false);
  const [backupStartedAtIso, setBackupStartedAtIso] = useState(null);
  const pollTimerRef = useRef(null);

  const backupStatusLabel = backupStatus?.status || "unknown";
  const backupMessage = backupStatus?.message || "";
  const lastBackupDisplay = backupStatus?.last_backup_at
    ? new Date(backupStatus.last_backup_at).toLocaleString()
    : "Not available";

  // Elapsed time display while running
  const elapsedDisplay = useMemo(() => {
    if (!isBackupRunning || !backupStartedAtIso) return null;
    const startMs = new Date(backupStartedAtIso).getTime();
    if (!Number.isFinite(startMs)) return null;
    const diff = Date.now() - startMs;
    if (diff < 0) return "0s";
    const s = Math.floor(diff / 1000);
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return m > 0 ? `${m}m ${ss}s` : `${ss}s`;
  }, [isBackupRunning, backupStartedAtIso, backupStatus?.last_backup_at]); // refresh on status updates too

  // Simple badge class helper (optional)
  const badgeClass =
    backupStatusLabel === "success"
      ? "settings-badge settings-badge-success"
      : backupStatusLabel === "running"
      ? "settings-badge settings-badge-running"
      : backupStatusLabel === "error" || backupStatusLabel === "failed"
      ? "settings-badge settings-badge-error"
      : "settings-badge";

  /* -----------------------------
      FETCH LICENSE INFO
  ------------------------------*/
  useEffect(() => {
    if (!accessToken || !selectedRestaurantId) return;

    const controller = new AbortController();

    const fetchLicense = async () => {
      try {
        setLicenseLoading(true);
        setLicenseError(null);

        const url = `${LICENSE_API_URL}?id=${encodeURIComponent(selectedRestaurantId)}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "<no body>");
          throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
        }

        const data = await res.json();
        const row = Array.isArray(data) ? data[0] || null : data || null;
        setLicenseInfo(row);
      } catch (err) {
        if (err.name === "AbortError") return;
        setLicenseError(err.message || "Failed to load license info");
        setLicenseInfo(null);
      } finally {
        setLicenseLoading(false);
      }
    };

    fetchLicense();
    return () => controller.abort();
  }, [accessToken, selectedRestaurantId]);

  /* -----------------------------
      FETCH TIMEZONE
  ------------------------------*/
  useEffect(() => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    const controller = new AbortController();

    const fetchTimezone = async () => {
      try {
        setTimezoneLoading(true);
        setTimezoneError(null);
        setTimezoneSuccess(null);

        const url = `${REDIR_BASE}${tenantServerUrl}/${TIMEZONE_API_URL}?id=${encodeURIComponent(
          selectedRestaurantId
        )}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "<no body>");
          throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
        }

        const data = await res.json();
        setTimezone(data?.timezone || "");
      } catch (err) {
        if (err.name === "AbortError") return;
        setTimezoneError(err.message || "Failed to load PHP timezone");
      } finally {
        setTimezoneLoading(false);
      }
    };

    fetchTimezone();
    return () => controller.abort();
  }, [accessToken, selectedRestaurantId, tenantServerUrl]);

  /* -----------------------------
      FETCH PAYMENT METHODS
  ------------------------------*/
  useEffect(() => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    const controller = new AbortController();

    const fetchPaymentMethods = async () => {
      try {
        setPaymentMethodsLoading(true);
        setPaymentMethodsError(null);
        setPaymentMethodsSuccess(null);

        const url = `${REDIR_BASE}${tenantServerUrl}/${PAYMENT_METHODS_API_URL}?id=${encodeURIComponent(
          selectedRestaurantId
        )}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "<no body>");
          throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
        }

        const data = await res.json();
        const methods = Array.isArray(data?.methods) ? data.methods : [];
        setPaymentMethods(methods);
        setPaymentGateways(Array.isArray(data?.gateways) ? data.gateways : []);
        setPaymentGatewaySamples(Array.isArray(data?.gatewaySamples) ? data.gatewaySamples : []);
        setPaymentPlaceholders(Array.isArray(data?.placeholders) ? data.placeholders : []);
      } catch (err) {
        if (err.name === "AbortError") return;
        setPaymentMethodsError(err.message || "Failed to load payment methods");
        setPaymentMethods([]);
        setPaymentGateways([]);
      } finally {
        setPaymentMethodsLoading(false);
      }
    };

    fetchPaymentMethods();
    return () => controller.abort();
  }, [accessToken, selectedRestaurantId, tenantServerUrl]);

  /* -----------------------------
      FETCH RESTAURANT TOOLS
  ------------------------------*/
  useEffect(() => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    const controller = new AbortController();

    const fetchRestaurantTools = async () => {
      try {
        setRestaurantToolsLoading(true);
        setRestaurantToolsError(null);
        setRestaurantToolsSuccess(null);

        const url = `${REDIR_BASE}${tenantServerUrl}/${RESTAURANT_TOOLS_API_URL}?id=${encodeURIComponent(
          selectedRestaurantId
        )}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "<no body>");
          throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
        }

        const data = await res.json();
        const tools = Array.isArray(data?.tools) ? data.tools : [];
        setRestaurantTools(tools);
      } catch (err) {
        if (err.name === "AbortError") return;
        setRestaurantToolsError(err.message || "Failed to load restaurant tools");
        setRestaurantTools([]);
      } finally {
        setRestaurantToolsLoading(false);
      }
    };

    fetchRestaurantTools();
    return () => controller.abort();
  }, [accessToken, selectedRestaurantId, tenantServerUrl]);

  /* -----------------------------
      SAVE PAYMENT METHODS
  ------------------------------*/
  const handlePaymentMethodsSave = async (e) => {
    e.preventDefault();
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    try {
      setPaymentMethodsSaving(true);
      setPaymentMethodsError(null);
      setPaymentMethodsSuccess(null);

      const payload = {
        id: selectedRestaurantId,
        methods: paymentMethods,
        gateways: paymentGateways,
      };

      const res = await fetch(`${REDIR_BASE}${tenantServerUrl}/${PAYMENT_METHODS_API_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      const data = await res.json().catch(() => null);
      if (Array.isArray(data?.methods)) {
        setPaymentMethods(data.methods);
      }
      if (Array.isArray(data?.gateways)) {
        setPaymentGateways(data.gateways);
      }
      if (Array.isArray(data?.gatewaySamples)) {
        setPaymentGatewaySamples(data.gatewaySamples);
      }
      setPaymentMethodsSuccess("Payment methods and gateways updated successfully.");
    } catch (err) {
      setPaymentMethodsError(err.message || "Failed to update payment methods");
    } finally {
      setPaymentMethodsSaving(false);
    }
  };

  /* -----------------------------
      SAVE RESTAURANT TOOLS
  ------------------------------*/
  const handleRestaurantToolsSave = async (e) => {
    e.preventDefault();
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    try {
      setRestaurantToolsSaving(true);
      setRestaurantToolsError(null);
      setRestaurantToolsSuccess(null);

      const payload = {
        id: selectedRestaurantId,
        tools: restaurantTools,
      };

      const res = await fetch(`${REDIR_BASE}${tenantServerUrl}/${RESTAURANT_TOOLS_API_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      const data = await res.json().catch(() => null);
      if (Array.isArray(data?.tools)) {
        setRestaurantTools(data.tools);
      }
      setRestaurantToolsSuccess("Restaurant tools updated successfully.");
    } catch (err) {
      setRestaurantToolsError(err.message || "Failed to update restaurant tools");
    } finally {
      setRestaurantToolsSaving(false);
    }
  };

  const updateRestaurantToolField = (code, patch) => {
    setRestaurantTools((prev) =>
      prev.map((tool) => (tool.code === code ? { ...tool, ...patch } : tool))
    );
  };

  const updatePaymentMethodField = (code, patch) => {
    setPaymentMethods((prev) =>
      prev.map((method) =>
        method.code === code ? { ...method, ...patch } : method
      )
    );
  };

  const onlinePaymentEnabled = paymentMethods.some(
    (method) => method.code === "online" && !!method.enabled
  );

  const updateGatewayField = (gatewayKey, patch) => {
    setPaymentGateways((prev) =>
      prev.map((gateway) =>
        gateway.gatewayKey === gatewayKey ? { ...gateway, ...patch } : gateway
      )
    );
  };

  const removeGateway = (gatewayKey) => {
    setPaymentGateways((prev) => prev.filter((gateway) => gateway.gatewayKey !== gatewayKey));
  };

  const addCustomGateway = () => {
    const stamp = Date.now().toString(36);
    setPaymentGateways((prev) => [
      ...prev,
      {
        gatewayKey: `custom_${stamp}`,
        name: "Custom Gateway",
        enabled: true,
        apiMethod: "POST",
        apiEndpoint: "https://",
        headersJson:
          '{\n  "Content-Type": "application/json",\n  "Authorization": "Bearer {{accessToken}}"\n}',
        payloadTemplate:
          '{\n  "amount": "{{amount}}",\n  "currency": "{{currency}}",\n  "orderId": "{{orderId}}",\n  "tableNumber": "{{tableNumber}}"\n}',
        sortOrder: prev.length + 1,
        isSample: false,
      },
    ]);
  };

  const addGatewayFromSample = (sample) => {
    if (!sample) return;
    const existingKeys = new Set(paymentGateways.map((g) => g.gatewayKey));
    let key = sample.gatewayKey || `sample_${Date.now().toString(36)}`;
    if (existingKeys.has(key)) {
      key = `${key}_${Date.now().toString(36)}`;
    }
    setPaymentGateways((prev) => [
      ...prev,
      {
        ...sample,
        gatewayKey: key,
        enabled: true,
        sortOrder: prev.length + 1,
        isSample: false,
      },
    ]);
  };

  /* -----------------------------
      SAVE TIMEZONE
  ------------------------------*/
  const handleTimezoneSave = async (e) => {
    e.preventDefault();
    if (!timezone || !accessToken || !selectedRestaurantId) return;

    try {
      setTimezoneSaving(true);
      setTimezoneError(null);
      setTimezoneSuccess(null);

      const payload = { id: selectedRestaurantId, timezone };

      const res = await fetch(`${REDIR_BASE}${tenantServerUrl}/${TIMEZONE_API_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      await res.json().catch(() => null);
      setTimezoneSuccess("Timezone updated successfully.");
    } catch (err) {
      setTimezoneError(err.message || "Failed to update timezone");
    } finally {
      setTimezoneSaving(false);
    }
  };

  /* -----------------------------
      FETCH BACKUP STATUS (function + polling)
  ------------------------------*/
  const fetchBackupStatus = async (signal) => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    try {
      setBackupStatusLoading(true);
      setBackupStatusError(null);

      const url = `${REDIR_BASE}${tenantServerUrl}/${BACKUP_STATUS_API_URL}?id=${encodeURIComponent(
        selectedRestaurantId
      )}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
        signal,
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      const data = await res.json();
      setBackupStatus(data || null);

      // Auto-stop running state if status is terminal
      const st = (data?.status || "").toLowerCase();
      if (st === "success" || st === "error" || st === "failed") {
        setIsBackupRunning(false);
        stopPolling();
      } else if (st === "running") {
        setIsBackupRunning(true);
        if (!backupStartedAtIso && data?.last_backup_at) {
          setBackupStartedAtIso(data.last_backup_at);
        }
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      setBackupStatusError(err.message || "Failed to load backup status");
      setBackupStatus(null);
    } finally {
      setBackupStatusLoading(false);
    }
  };

  const startPolling = () => {
    stopPolling(); // ensure no duplicates
    pollTimerRef.current = setInterval(() => {
      const controller = new AbortController();
      fetchBackupStatus(controller.signal);
      // no need to abort since it's short-lived, but safe to keep pattern consistent
    }, 2000);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Initial fetch + decide whether to poll
  useEffect(() => {
    if (!accessToken || !selectedRestaurantId || !tenantServerUrl) return;

    const controller = new AbortController();
    fetchBackupStatus(controller.signal);

    return () => {
      controller.abort();
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, selectedRestaurantId, tenantServerUrl]);

  // If status becomes "running" (from initial fetch), start polling
  useEffect(() => {
    const st = (backupStatus?.status || "").toLowerCase();
    if (st === "running") {
      setIsBackupRunning(true);
      setBackupStartedAtIso(backupStatus?.last_backup_at || new Date().toISOString());
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backupStatus?.status]);

  /* -----------------------------
      MANUAL "START BACKUP" HANDLER
  ------------------------------*/
  const handleStartBackup = async () => {
    if (!accessToken || !tenantServerUrl || !selectedRestaurantId) return;

    try {
      setBackupTriggerLoading(true);
      setBackupTriggerError(null);
      setBackupTriggerSuccess(null);

      const url = `${REDIR_BASE}${tenantServerUrl}/${RUN_BACKUP_API_URL}`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => "<no body>");
        throw new Error(`HTTP ${res.status} ${res.statusText} – ${bodyText}`);
      }

      const data = await res.json().catch(() => ({}));
      setBackupTriggerSuccess(data?.message || "Backup started.");

      // Immediately set UI to running and begin polling
      const startedIso = new Date().toISOString();
      setIsBackupRunning(true);
      setBackupStartedAtIso(startedIso);

      setBackupStatus((prev) => ({
        ...(prev || {}),
        status: "running",
        message: "Backup started manually.",
        last_backup_at: startedIso,
      }));

      // Poll for progress/terminal status
      startPolling();
    } catch (err) {
      setBackupTriggerError(err.message || "Failed to start backup");
      setIsBackupRunning(false);
      stopPolling();
    } finally {
      setBackupTriggerLoading(false);
    }
  };

  /* -----------------------------
      DISPLAY HELPERS
  ------------------------------*/
  const restaurantIdDisplay = licenseInfo?.id ?? selectedRestaurantId ?? "—";
  const statusDisplay = licenseInfo?.licence_status || "Not available";
  const renewalDisplay =
    licenseInfo?.licence_expiry && licenseInfo.licence_expiry !== "0000-00-00"
      ? licenseInfo.licence_expiry
      : "Not set";

  const licenseKeyDisplay =
    showLicenseKey && licenseInfo?.licence
      ? licenseInfo.licence
      : licenseInfo?.licence
      ? "••••••••••••••••"
      : "Not available";

  /* -----------------------------
      RENDER
  ------------------------------*/
  return (
    <div className="details-container-page settings-page">
      <Navbar pageTitle={pageTitle} onToggleSidebar={true} />

      <div className="details-main settings-main">
        <div className="settings-grid">
          {/* LEFT COLUMN */}
          <div className="settings-column">
            {/* DATABASE BACKUP SETTINGS */}
            <section className="settings-card">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Database Backup</h2>
              </div>

              <div className="settings-field">
                <span className="settings-label">Last Backup:</span>
                <span className="settings-value">
                  {backupStatusLoading ? "Checking..." : backupStatusError ? "Unavailable" : lastBackupDisplay}
                </span>
              </div>

              <div className="settings-field">
                <span className="settings-label">Status:</span>
                <span className="settings-value">
                  <span className={badgeClass}>{backupStatusLabel}</span>
                  {isBackupRunning && elapsedDisplay ? (
                    <span style={{ marginLeft: 10, opacity: 0.8 }}>
                      • running for {elapsedDisplay}
                    </span>
                  ) : null}
                </span>
              </div>

              {backupMessage ? (
                <div className="settings-field">
                  <span className="settings-label">Message:</span>
                  <span className="settings-value">{backupMessage}</span>
                </div>
              ) : null}

              {/* Progress indicator */}
              {isBackupRunning ? (
                <div className="settings-field">
                  <span className="settings-label">Progress:</span>
                  <span className="settings-value" style={{ width: "100%" }}>
                    <div className="tm-progress">
                      <div className="tm-progress-bar-indeterminate" />
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
                      Backing up… (this may take a few minutes)
                    </div>
                  </span>
                </div>
              ) : null}

              {backupTriggerError && (
                <div className="settings-field">
                  <span className="settings-label">Backup Error</span>
                  <span className="settings-value settings-error">{backupTriggerError}</span>
                </div>
              )}

              {backupTriggerSuccess && (
                <div className="settings-field">
                  <span className="settings-label">Backup Info</span>
                  <span className="settings-value settings-success">{backupTriggerSuccess}</span>
                </div>
              )}

              <div className="settings-backup-actions">
                <button
                  className="tm-btn tm-btn-primary"
                  type="button"
                  onClick={handleStartBackup}
                  disabled={backupTriggerLoading || isBackupRunning}
                  title={isBackupRunning ? "Backup is currently running" : "Start a manual backup"}
                >
                  {backupTriggerLoading ? "Starting..." : isBackupRunning ? "Backup Running..." : "Start Backup"}
                </button>

                <button
                  className="tm-btn tm-btn-light"
                  type="button"
                  onClick={() => {
                    const controller = new AbortController();
                    fetchBackupStatus(controller.signal);
                  }}
                >
                  Refresh Status
                </button>

                {/* Optional: if you create a getBackupLog.php endpoint, wire it here */}
                {/* <button className="tm-btn tm-btn-dark" type="button" onClick={handleViewLog}>
                  View Log
                </button> */}
              </div>
            </section>

            {/* TIMEZONE SETTINGS */}
            <section className="settings-card">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Timezone</h2>
              </div>

              <p className="settings-main-label">
                This timezone will be used by your backend for reports, scheduled tasks, and date/time calculations.
              </p>

              {timezoneLoading ? (
                <div className="settings-field">
                  <span className="settings-label">Timezone</span>
                  <span className="settings-value">Loading...</span>
                </div>
              ) : (
                <form onSubmit={handleTimezoneSave}>
                  <div className="settings-field settings-backup-row">
                    <span className="settings-label">Timezone</span>
                    <select
                      className="settings-select"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                    >
                      <option value="">Select timezone…</option>
                      {commonTimezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>

                  {timezoneError && (
                    <div className="settings-field">
                      <span className="settings-label">Error</span>
                      <span className="settings-value settings-error">{timezoneError}</span>
                    </div>
                  )}

                  {timezoneSuccess && (
                    <div className="settings-field">
                      <span className="settings-label">Status</span>
                      <span className="settings-value settings-success">{timezoneSuccess}</span>
                    </div>
                  )}

                  <button
                    className="tm-btn tm-btn-primary tm-btn-full"
                    type="submit"
                    disabled={!timezone || timezoneSaving}
                  >
                    {timezoneSaving ? "Saving..." : "Save Timezone"}
                  </button>
                </form>
              )}
            </section>

            {["admin", "manager"].includes(String(user?.role || "").toLowerCase()) && (
              <AiSettingsCard accessToken={accessToken} restaurantId={selectedRestaurantId} />
            )}

            {/* PAYMENT METHODS */}
            <section className="settings-card">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Payment Methods</h2>
              </div>

              <p className="settings-main-label">
                Choose which payment options appear in the customer app and when staff override unsettled tabs in Admin.
              </p>

              {paymentMethodsLoading ? (
                <div className="settings-field">
                  <span className="settings-label">Methods</span>
                  <span className="settings-value">Loading...</span>
                </div>
              ) : (
                <form onSubmit={handlePaymentMethodsSave}>
                  <div className="settings-payment-list">
                    {paymentMethods.map((method) => (
                      <div key={method.code} className="settings-payment-row">
                        <label className="settings-payment-enable">
                          <input
                            type="checkbox"
                            checked={!!method.enabled}
                            onChange={(e) =>
                              updatePaymentMethodField(method.code, {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="settings-payment-code">{method.code}</span>
                        </label>
                        <input
                          className="settings-input"
                          type="text"
                          value={method.label || ""}
                          onChange={(e) =>
                            updatePaymentMethodField(method.code, {
                              label: e.target.value,
                            })
                          }
                          placeholder="Display label"
                          maxLength={60}
                        />
                      </div>
                    ))}
                    {paymentMethods.length === 0 && (
                      <div className="settings-field">
                        <span className="settings-value settings-error">
                          No payment methods returned from the restaurant server.
                        </span>
                      </div>
                    )}
                  </div>

                  {onlinePaymentEnabled && (
                    <div className="settings-gateway-panel">
                      <h3 className="settings-gateway-title">Online payment gateways</h3>
                      <p className="settings-main-label">
                        Add one or more gateways. Use placeholders in headers/payload so the app can
                        fill amount, table, and customer at checkout.
                      </p>
                      {paymentPlaceholders.length > 0 && (
                        <p className="settings-gateway-placeholders">
                          Placeholders: {paymentPlaceholders.join(" ")}
                        </p>
                      )}

                      <div className="settings-gateway-actions">
                        <button
                          type="button"
                          className="tm-btn tm-btn-light"
                          onClick={addCustomGateway}
                        >
                          Add custom gateway
                        </button>
                        {paymentGatewaySamples.map((sample) => (
                          <button
                            key={sample.gatewayKey}
                            type="button"
                            className="tm-btn tm-btn-dark"
                            onClick={() => addGatewayFromSample(sample)}
                          >
                            Add {sample.name}
                          </button>
                        ))}
                        <Link
                          to="/settings/payment-gateway-log"
                          className="tm-btn tm-btn-primary"
                        >
                          View gateway activity
                        </Link>
                      </div>

                      {paymentGateways.length === 0 && (
                        <div className="settings-field">
                          <span className="settings-value">
                            No gateways yet. Add a sample (PayPal / Stripe / Square) or a custom gateway.
                          </span>
                        </div>
                      )}

                      {paymentGateways.map((gateway) => (
                        <div key={gateway.gatewayKey} className="settings-gateway-card">
                          <div className="settings-payment-row">
                            <label className="settings-payment-enable">
                              <input
                                type="checkbox"
                                checked={!!gateway.enabled}
                                onChange={(e) =>
                                  updateGatewayField(gateway.gatewayKey, {
                                    enabled: e.target.checked,
                                  })
                                }
                              />
                              <span className="settings-payment-code">enabled</span>
                            </label>
                            <input
                              className="settings-input"
                              type="text"
                              value={gateway.name || ""}
                              onChange={(e) =>
                                updateGatewayField(gateway.gatewayKey, {
                                  name: e.target.value,
                                })
                              }
                              placeholder="Gateway display name"
                              maxLength={120}
                            />
                            <button
                              type="button"
                              className="tm-btn tm-btn-danger"
                              onClick={() => removeGateway(gateway.gatewayKey)}
                            >
                              Remove
                            </button>
                          </div>

                          <div className="settings-gateway-grid">
                            <label className="settings-gateway-field">
                              <span>Key</span>
                              <input
                                className="settings-input"
                                type="text"
                                value={gateway.gatewayKey || ""}
                                readOnly
                              />
                            </label>
                            <label className="settings-gateway-field">
                              <span>HTTP method</span>
                              <select
                                className="settings-select"
                                value={gateway.apiMethod || "POST"}
                                onChange={(e) =>
                                  updateGatewayField(gateway.gatewayKey, {
                                    apiMethod: e.target.value,
                                  })
                                }
                              >
                                <option value="POST">POST</option>
                                <option value="GET">GET</option>
                                <option value="PUT">PUT</option>
                                <option value="PATCH">PATCH</option>
                              </select>
                            </label>
                          </div>

                          <label className="settings-gateway-field">
                            <span>API endpoint</span>
                            <input
                              className="settings-input"
                              type="text"
                              value={gateway.apiEndpoint || ""}
                              onChange={(e) =>
                                updateGatewayField(gateway.gatewayKey, {
                                  apiEndpoint: e.target.value,
                                })
                              }
                              placeholder="https://api.example.com/checkout"
                            />
                          </label>

                          <label className="settings-gateway-field">
                            <span>Headers JSON</span>
                            <textarea
                              className="settings-textarea"
                              rows={4}
                              value={gateway.headersJson || ""}
                              onChange={(e) =>
                                updateGatewayField(gateway.gatewayKey, {
                                  headersJson: e.target.value,
                                })
                              }
                              placeholder='{"Authorization":"Bearer {{accessToken}}"}'
                            />
                          </label>

                          <label className="settings-gateway-field">
                            <span>Payload template</span>
                            <textarea
                              className="settings-textarea"
                              rows={8}
                              value={gateway.payloadTemplate || ""}
                              onChange={(e) =>
                                updateGatewayField(gateway.gatewayKey, {
                                  payloadTemplate: e.target.value,
                                })
                              }
                              placeholder='{"amount":"{{amount}}","currency":"{{currency}}"}'
                            />
                          </label>
                        </div>
                      ))}
                    </div>
                  )}

                  {paymentMethodsError && (
                    <div className="settings-field">
                      <span className="settings-label">Error</span>
                      <span className="settings-value settings-error">{paymentMethodsError}</span>
                    </div>
                  )}

                  {paymentMethodsSuccess && (
                    <div className="settings-field">
                      <span className="settings-label">Status</span>
                      <span className="settings-value settings-success">{paymentMethodsSuccess}</span>
                    </div>
                  )}

                  <div className="settings-actions-row" style={{ marginTop: 12 }}>
                    <button
                      className="tm-btn tm-btn-primary"
                      type="submit"
                      disabled={paymentMethodsSaving || paymentMethods.length === 0}
                    >
                      {paymentMethodsSaving ? "Saving..." : "Save Payment Methods"}
                    </button>
                    <Link to="/settings/payment-gateway-log" className="tm-btn tm-btn-light">
                      Gateway activity log
                    </Link>
                    <Link to="/data/day-end-audit" className="tm-btn tm-btn-dark">
                      Day-end financial audit
                    </Link>
                  </div>
                </form>
              )}
            </section>

            {/* RESTAURANT TOOLS */}
            <section className="settings-card">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Restaurant Tools</h2>
              </div>

              <p className="settings-main-label">
                Enable or disable customer-app tools for this restaurant. Turning off Split Bill hides it on the
                dashboard settlement sheet and the Bill screen.
              </p>

              {restaurantToolsLoading ? (
                <div className="settings-field">
                  <span className="settings-label">Tools</span>
                  <span className="settings-value">Loading...</span>
                </div>
              ) : (
                <form onSubmit={handleRestaurantToolsSave}>
                  <div className="settings-payment-list">
                    {restaurantTools.map((tool) => (
                      <div key={tool.code} className="settings-payment-row">
                        <label className="settings-payment-enable">
                          <input
                            type="checkbox"
                            checked={!!tool.enabled}
                            onChange={(e) =>
                              updateRestaurantToolField(tool.code, {
                                enabled: e.target.checked,
                              })
                            }
                          />
                          <span className="settings-payment-code">{tool.code}</span>
                        </label>
                        <input
                          className="settings-input"
                          type="text"
                          value={tool.label || ""}
                          onChange={(e) =>
                            updateRestaurantToolField(tool.code, {
                              label: e.target.value,
                            })
                          }
                          placeholder="Display label"
                          maxLength={60}
                        />
                      </div>
                    ))}
                    {restaurantTools.length === 0 && (
                      <div className="settings-field">
                        <span className="settings-value settings-error">
                          No restaurant tools returned from the restaurant server.
                        </span>
                      </div>
                    )}
                  </div>

                  {restaurantToolsError && (
                    <div className="settings-field">
                      <span className="settings-label">Error</span>
                      <span className="settings-value settings-error">{restaurantToolsError}</span>
                    </div>
                  )}

                  {restaurantToolsSuccess && (
                    <div className="settings-field">
                      <span className="settings-label">Status</span>
                      <span className="settings-value settings-success">{restaurantToolsSuccess}</span>
                    </div>
                  )}

                  <button
                    className="tm-btn tm-btn-primary tm-btn-full"
                    type="submit"
                    disabled={restaurantToolsSaving || restaurantTools.length === 0}
                  >
                    {restaurantToolsSaving ? "Saving..." : "Save Restaurant Tools"}
                  </button>
                </form>
              )}
            </section>

            {/* MANAGE RATING QUESTIONS */}
            <section className="settings-card settings-card-actions">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Manage Rating Questions</h2>
              </div>
              <span className="settings-main-label">
                These are the questions that will display in the Table Mate! app to your customers.
              </span>
              <br />
              <span className="settings-main-label">
                <i>
                  * Changing a question&apos;s content will start a new rating thread, keeping old responses separate in
                  the Data Centre.
                </i>{" "}
                <a href="https://support.tablemate.net" target="_blank" rel="noreferrer">
                  Learn More
                </a>
              </span>
              <br />
              <Link to="/settings/questions" className="tm-btn tm-btn-danger tm-btn-full">
                Edit Questions
              </Link>
              <Link to="/data/6/0/0" className="tm-btn tm-btn-light tm-btn-full">
                Browse Data
              </Link>
            </section>
          </div>

          {/* RIGHT COLUMN */}
          <div className="settings-column">
            {/* LICENSE SETTINGS */}
            <section className="settings-card">
              <div className="settings-card-header">
                <h2 className="settings-card-title">License Settings</h2>
              </div>

              {licenseLoading && (
                <div className="settings-field">
                  <span className="settings-label">License</span>
                  <span className="settings-value">Loading...</span>
                </div>
              )}

              {licenseError && (
                <div className="settings-field">
                  <span className="settings-label">License Error</span>
                  <span className="settings-value settings-error">{licenseError}</span>
                </div>
              )}

              {!licenseLoading && !licenseError && (
                <>
                  <div className="settings-field settings-license-row">
                    <span className="settings-label">Restaurant ID</span>
                    <span className="settings-value">{restaurantIdDisplay}</span>
                    <button
                      className="tm-btn tm-btn-light"
                      type="button"
                      onClick={() => setShowLicenseKey((prev) => !prev)}
                    >
                      {showLicenseKey ? "Hide License Key" : "View License Key"}
                    </button>
                  </div>

                  <div className="settings-field">
                    <span className="settings-label">License Key</span>
                    <span className="settings-value">{licenseKeyDisplay}</span>
                  </div>

                  <div className="settings-field settings-license-row">
                    <span className="settings-label">Status</span>
                    <span className="settings-value">{statusDisplay}</span>
                    <Link to="/license">
                      <button className="tm-btn tm-btn-light" type="button">
                        More Details
                      </button>
                    </Link>
                  </div>

                  <div className="settings-field">
                    <span className="settings-label">Renewal</span>
                    <span className="settings-value">{renewalDisplay}</span>
                  </div>
                </>
              )}
            </section>

            {/* HELP / SUPPORT */}
            <section className="settings-card settings-card-actions">
              <div className="settings-card-header">
                <h2 className="settings-card-title">Need Help?</h2>
              </div>
              <span className="settings-label">Feel free to contact our dedicated customer support team.</span>
              <br />

              <a href="https://tablemate.net/#contactus" target="_blank" rel="noreferrer">
                <button className="tm-btn tm-btn-danger tm-btn-full">Contact Table Mate Support</button>
              </a>

              <a href="https://support.tablemate.net" target="_blank" rel="noreferrer">
                <button className="tm-btn tm-btn-dark tm-btn-full">
                  Visit our detailed application documentation
                </button>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
